import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowLeftRight,
  TrendingUp,
  History,
  Globe2,
  Bell,
  WifiOff,
  ShieldCheck,
  Zap,
  Copy,
  Check,
  Share2,
  Info,
  Calendar,
  Layers,
  Database,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Search,
  Sparkles,
  Sliders,
  CheckCircle2,
  AlertTriangle,
  RotateCcw,
  Clock,
  Lock,
  ArrowUpRight,
  ArrowDownRight,
  ArrowRight,
  FileText,
  Flame
} from 'lucide-react';
import { CurrencyMetadata, FxObservation, FxConversionResult, FxRateAlert, FxSeoPairEligibility, AdrRecord } from '../../types';
import { GLOBAL_CURRENCIES_LIST, CURRENCIES_BY_CODE } from '../../data/fxCurrenciesData';
import { fxEngine, financialFormat, formatRateDisplay } from '../../services/fxEngine';
import { SEO_ELIGIBLE_PAIRS, FX_ADR_RECORDS, PROMPT_04_DELIVERABLES } from '../../data/fxSeoData';
import { MarketHeatmap } from './MarketHeatmap';

interface CurrencyIntelligenceHubProps {
  onNavigateHome: () => void;
  onNavigateGovernance: () => void;
  onNavigateArchitecture: () => void;
}

type FxHubTab =
  | 'converter'
  | 'heatmap'
  | 'charts'
  | 'directory'
  | 'pair-deep-dive'
  | 'watchlist'
  | 'alerts'
  | 'offline-pwa'
  | 'methodology'
  | 'command-center'
  | 'deliverables-p4';

export const CurrencyIntelligenceHub: React.FC<CurrencyIntelligenceHubProps> = ({
  onNavigateHome,
  onNavigateGovernance,
  onNavigateArchitecture,
}) => {
  const [activeTab, setActiveTab] = useState<FxHubTab>('converter');

  // Converter States
  const [fromCurrency, setFromCurrency] = useState<string>('USD');
  const [toCurrency, setToCurrency] = useState<string>('EUR');
  const [amount, setAmount] = useState<number>(1000);
  const [copied, setCopied] = useState<boolean>(false);
  const [isOfflineSimulated, setIsOfflineSimulated] = useState<boolean>(false);

  // Currency Dropdown search states
  const [fromSearchQuery, setFromSearchQuery] = useState('');
  const [toSearchQuery, setToSearchQuery] = useState('');
  const [fromDropdownOpen, setFromDropdownOpen] = useState(false);
  const [toDropdownOpen, setToDropdownOpen] = useState(false);

  // Timeframe and historical state
  const [chartTimeframe, setChartTimeframe] = useState<'1D' | '5D' | '1M' | '3M' | '6M' | 'YTD' | '1Y' | '5Y' | 'MAX'>('1M');
  const [historicalDate, setHistoricalDate] = useState<string>('2025-01-15');
  const [hoveredPointIndex, setHoveredPointIndex] = useState<number | null>(null);

  // Directory filter
  const [directorySearch, setDirectorySearch] = useState('');
  const [directoryRegionFilter, setDirectoryRegionFilter] = useState<string>('ALL');

  // Pair Deep-Dive Selector
  const [selectedPairCode, setSelectedPairCode] = useState<string>('USD-EUR');

  // Watchlist & Favorites
  const [favoritesList, setFavoritesList] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('yamoopay_fx_favorites');
      return stored ? JSON.parse(stored) : ['USD/EUR', 'GBP/USD', 'USD/JPY', 'USD/CAD', 'USD/INR', 'EUR/XAF'];
    } catch {
      return ['USD/EUR', 'GBP/USD', 'USD/JPY', 'USD/CAD'];
    }
  });

  // Alerts State
  const [alerts, setAlerts] = useState<FxRateAlert[]>(() => fxEngine.getAlerts());
  const [alertFormPair, setAlertFormPair] = useState<string>('USD/EUR');
  const [alertFormCondition, setAlertFormCondition] = useState<'ABOVE' | 'BELOW' | 'PERCENT_CHANGE'>('ABOVE');
  const [alertFormTarget, setAlertFormTarget] = useState<number>(0.95);
  const [alertSuccessMsg, setAlertSuccessMsg] = useState<string | null>(null);

  // Command Center Telemetry
  const [telemetry, setTelemetry] = useState(() => fxEngine.getTelemetry());

  // URL Query Sync
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qFrom = params.get('from');
      const qTo = params.get('to');
      const qAmount = params.get('amount');
      if (qFrom && CURRENCIES_BY_CODE.has(qFrom.toUpperCase())) {
        setFromCurrency(qFrom.toUpperCase());
      }
      if (qTo && CURRENCIES_BY_CODE.has(qTo.toUpperCase())) {
        setToCurrency(qTo.toUpperCase());
      }
      if (qAmount && !isNaN(Number(qAmount)) && Number(qAmount) > 0) {
        setAmount(Number(qAmount));
      }
    } catch {
      // Ignore in iframe sandboxes
    }
  }, []);

  // Save favorites to storage
  useEffect(() => {
    try {
      localStorage.setItem('yamoopay_fx_favorites', JSON.stringify(favoritesList));
    } catch {
      // Ignore
    }
  }, [favoritesList]);

  // Current conversion result calculation
  const conversionResult: FxConversionResult = useMemo(() => {
    return fxEngine.convert(amount, fromCurrency, toCurrency, isOfflineSimulated);
  }, [amount, fromCurrency, toCurrency, isOfflineSimulated]);

  // Historical Series calculation for current pair
  const historicalSeries = useMemo(() => {
    return fxEngine.getHistoricalSeries(fromCurrency, toCurrency, chartTimeframe);
  }, [fromCurrency, toCurrency, chartTimeframe]);

  // Exact date lookup
  const historicalDateLookup = useMemo(() => {
    return fxEngine.getHistoricalRateOnDate(fromCurrency, toCurrency, historicalDate);
  }, [fromCurrency, toCurrency, historicalDate]);

  // Swap currencies handler
  const handleSwap = () => {
    const prevFrom = fromCurrency;
    const prevTo = toCurrency;
    setFromCurrency(prevTo);
    setToCurrency(prevFrom);
  };

  // Copy result
  const handleCopyResult = () => {
    const textToCopy = `${financialFormat(amount, fromCurrency)} ${fromCurrency} = ${financialFormat(conversionResult.convertedAmount, toCurrency)} ${toCurrency} (Rate: 1 ${fromCurrency} = ${formatRateDisplay(conversionResult.rate)} ${toCurrency})`;
    navigator.clipboard?.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Toggle favorite
  const handleToggleFavorite = (pairKey: string) => {
    setFavoritesList((prev) =>
      prev.includes(pairKey) ? prev.filter((p) => p !== pairKey) : [...prev, pairKey]
    );
  };

  // Filtered Currencies for Dropdowns
  const filteredFromCurrencies = useMemo(() => {
    const q = fromSearchQuery.toLowerCase().trim();
    if (!q) return GLOBAL_CURRENCIES_LIST;
    return GLOBAL_CURRENCIES_LIST.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.localizedName.toLowerCase().includes(q) ||
        c.aliases.some((a) => a.toLowerCase().includes(q))
    );
  }, [fromSearchQuery]);

  const filteredToCurrencies = useMemo(() => {
    const q = toSearchQuery.toLowerCase().trim();
    if (!q) return GLOBAL_CURRENCIES_LIST;
    return GLOBAL_CURRENCIES_LIST.filter(
      (c) =>
        c.code.toLowerCase().includes(q) ||
        c.name.toLowerCase().includes(q) ||
        c.localizedName.toLowerCase().includes(q) ||
        c.aliases.some((a) => a.toLowerCase().includes(q))
    );
  }, [toSearchQuery]);

  // Directory filter
  const filteredDirectoryCurrencies = useMemo(() => {
    return GLOBAL_CURRENCIES_LIST.filter((c) => {
      const matchesSearch =
        !directorySearch ||
        c.code.toLowerCase().includes(directorySearch.toLowerCase()) ||
        c.name.toLowerCase().includes(directorySearch.toLowerCase()) ||
        c.countries.some((cntry) => cntry.toLowerCase().includes(directorySearch.toLowerCase()));
      const matchesRegion = directoryRegionFilter === 'ALL' || c.region === directoryRegionFilter;
      return matchesSearch && matchesRegion;
    });
  }, [directorySearch, directoryRegionFilter]);

  // Popular amounts table
  const popularAmounts = [1, 10, 50, 100, 500, 1000, 5000, 10000];

  const currentFromMeta = CURRENCIES_BY_CODE.get(fromCurrency) || GLOBAL_CURRENCIES_LIST[0];
  const currentToMeta = CURRENCIES_BY_CODE.get(toCurrency) || GLOBAL_CURRENCIES_LIST[1];

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8">
      {/* 1. Header Banner & Context Navigation */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#E5E7EB] pb-6">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-[#F0FDF4] text-[#0E7044] border border-[#DCFCE7] flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[#0E7044] animate-pulse"></span>
              Multi-Source FX Engine
            </span>
            <span className="text-xs text-[#6B7280]">170+ Currencies • Zero JS Float Drift</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1A1A1A] tracking-tight">
            YamooPay Global Currency Intelligence
          </h1>
          <p className="text-xs sm:text-sm text-[#4B5563]">
            Single System of Record for wholesale mid-market rates, statutory central bank fixings, sovereign peg rules, and historical analytics.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setIsOfflineSimulated((prev) => !prev)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors flex items-center gap-1.5 cursor-pointer ${
              isOfflineSimulated
                ? 'bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]'
                : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:bg-[#F9FAFB]'
            }`}
            title="Simulate network disconnection and inspect Last Known Good (LKG) cached conversions"
          >
            <WifiOff className="w-3.5 h-3.5" />
            <span>{isOfflineSimulated ? 'Offline Mode Active' : 'Simulate Offline'}</span>
          </button>

          <button
            onClick={onNavigateGovernance}
            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-[#F0F4FF] text-[#1E40AF] border border-[#C7D7FE] hover:bg-[#E0EAFF] transition-colors flex items-center gap-1.5 cursor-pointer"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Data Trust Center</span>
          </button>
        </div>
      </div>

      {/* Offline Banner if Simulated */}
      {isOfflineSimulated && (
        <div className="p-3.5 bg-[#FEF3F2] border border-[#FECDCA] rounded-xl flex items-start sm:items-center justify-between gap-3 text-xs text-[#B42318] animate-in fade-in duration-150">
          <div className="flex items-center gap-2">
            <WifiOff className="w-4 h-4 shrink-0" />
            <div>
              <span className="font-bold">You are currently in Offline Mode.</span> Rates displayed are previously validated Last Known Good (LKG) observations stored securely in local device storage.
            </div>
          </div>
          <button
            onClick={() => setIsOfflineSimulated(false)}
            className="px-2.5 py-1 bg-white border border-[#FECDCA] text-[#B42318] rounded-md font-bold hover:bg-[#FEE4E2] transition-colors cursor-pointer shrink-0"
          >
            Reconnect
          </button>
        </div>
      )}

      {/* 2. Top-Level Domain Feature Navigation Tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-2 border-b border-[#E5E7EB] no-scrollbar">
        {[
          { id: 'converter', label: 'Universal Converter', icon: ArrowLeftRight },
          { id: 'heatmap', label: 'Market Heatmap', icon: Flame },
          { id: 'charts', label: 'Historical Charts & Dates', icon: History },
          { id: 'directory', label: '170+ Currency Directory', icon: Globe2 },
          { id: 'pair-deep-dive', label: 'Pair Intelligence', icon: TrendingUp },
          { id: 'watchlist', label: 'My Watchlist', icon: Sparkles },
          { id: 'alerts', label: 'Rate Alerts', icon: Bell },
          { id: 'offline-pwa', label: 'Offline / PWA Engine', icon: WifiOff },
          { id: 'methodology', label: 'Methodology & Spreads', icon: Info },
          { id: 'command-center', label: 'FX Command Center', icon: Zap },
          { id: 'deliverables-p4', label: 'Architecture & ADRs', icon: FileText },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as FxHubTab)}
              className={`px-3.5 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-2 cursor-pointer ${
                isActive
                  ? 'bg-[#1A1A1A] text-white shadow-xs'
                  : 'bg-white text-[#4B5563] border border-[#E5E7EB] hover:border-[#1A1A1A] hover:text-[#1A1A1A]'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* 3. TAB 1: UNIVERSAL CONVERTER & POPULAR MATRIX */}
      {activeTab === 'converter' && (
        <div className="space-y-8 animate-in fade-in duration-150">
          {/* Main Card */}
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#E5E7EB] pb-4">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#0E7044]"></span>
                <span className="text-xs font-bold text-[#1A1A1A]">
                  Verified Mid-Market Conversion
                </span>
                <span className="text-[11px] text-[#6B7280]">
                  • 1 {fromCurrency} = {formatRateDisplay(conversionResult.rate)} {toCurrency}
                </span>
              </div>
              <div className="text-[11px] text-[#6B7280] flex items-center gap-1.5">
                <Clock className="w-3 h-3 text-[#9CA3AF]" />
                <span>{conversionResult.sourceProvenance}</span>
              </div>
            </div>

            {/* Input Controls Grid */}
            <div className="grid sm:grid-cols-12 gap-4 items-center">
              {/* Amount Input */}
              <div className="sm:col-span-4 space-y-1.5">
                <label className="block text-xs font-bold text-[#4B5563]">
                  Amount ({currentFromMeta.symbol})
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={amount === 0 ? '' : amount}
                    onChange={(e) => setAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                    className="w-full px-3.5 py-3 text-lg font-bold text-[#1A1A1A] bg-[#FBFBFC] border border-[#E5E7EB] rounded-xl focus:bg-white focus:border-[#1A1A1A] focus:ring-1 focus:ring-[#1A1A1A] outline-hidden transition-all"
                    placeholder="1,000"
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-[#6B7280]">
                    {fromCurrency}
                  </span>
                </div>
              </div>

              {/* From Currency Selector */}
              <div className="sm:col-span-3 space-y-1.5 relative">
                <label className="block text-xs font-bold text-[#4B5563]">From</label>
                <button
                  type="button"
                  onClick={() => {
                    setFromDropdownOpen((prev) => !prev);
                    setToDropdownOpen(false);
                  }}
                  className="w-full px-3.5 py-3 text-left font-bold text-sm text-[#1A1A1A] bg-[#FBFBFC] border border-[#E5E7EB] rounded-xl flex items-center justify-between hover:bg-white hover:border-[#9CA3AF] transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-2 truncate">
                    <span className="text-base">{currentFromMeta.flagEmoji}</span>
                    <span className="font-extrabold">{currentFromMeta.code}</span>
                    <span className="text-xs text-[#6B7280] truncate font-normal">
                      {currentFromMeta.name}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-[#6B7280] shrink-0" />
                </button>

                {/* Dropdown Menu */}
                {fromDropdownOpen && (
                  <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-xl shadow-lg max-h-64 overflow-y-auto p-2 space-y-1">
                    <div className="p-1 sticky top-0 bg-white border-b border-[#E5E7EB]">
                      <input
                        type="text"
                        placeholder="Search currency, code or country..."
                        value={fromSearchQuery}
                        onChange={(e) => setFromSearchQuery(e.target.value)}
                        className="w-full px-2.5 py-1.5 text-xs bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg focus:outline-hidden"
                        autoFocus
                      />
                    </div>
                    {filteredFromCurrencies.map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => {
                          setFromCurrency(c.code);
                          setFromDropdownOpen(false);
                          setFromSearchQuery('');
                        }}
                        className={`w-full px-2.5 py-2 text-left rounded-lg text-xs flex items-center justify-between hover:bg-[#F3F4F6] cursor-pointer ${
                          fromCurrency === c.code ? 'bg-[#F0F4FF] text-[#1E40AF] font-bold' : 'text-[#1A1A1A]'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span>{c.flagEmoji}</span>
                          <span className="font-bold">{c.code}</span>
                          <span className="text-[#6B7280] text-[11px] truncate max-w-[140px]">
                            {c.name}
                          </span>
                        </div>
                        {c.isMajor && (
                          <span className="text-[9px] font-bold bg-[#E5E7EB] px-1.5 py-0.5 rounded text-[#374151]">
                            Major
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Swap Button */}
              <div className="sm:col-span-2 flex justify-center sm:pt-6">
                <button
                  type="button"
                  onClick={handleSwap}
                  className="p-3 rounded-xl border border-[#E5E7EB] bg-white text-[#1A1A1A] hover:bg-[#F3F4F6] hover:border-[#1A1A1A] transition-all cursor-pointer shadow-xs"
                  title="Swap currencies (Instant recalculation)"
                  aria-label="Swap Currencies"
                >
                  <ArrowLeftRight className="w-4 h-4" />
                </button>
              </div>

              {/* To Currency Selector */}
              <div className="sm:col-span-3 space-y-1.5 relative">
                <label className="block text-xs font-bold text-[#4B5563]">To</label>
                <button
                  type="button"
                  onClick={() => {
                    setToDropdownOpen((prev) => !prev);
                    setFromDropdownOpen(false);
                  }}
                  className="w-full px-3.5 py-3 text-left font-bold text-sm text-[#1A1A1A] bg-[#FBFBFC] border border-[#E5E7EB] rounded-xl flex items-center justify-between hover:bg-white hover:border-[#9CA3AF] transition-all cursor-pointer"
                >
                  <div className="flex items-center gap-2 truncate">
                    <span className="text-base">{currentToMeta.flagEmoji}</span>
                    <span className="font-extrabold">{currentToMeta.code}</span>
                    <span className="text-xs text-[#6B7280] truncate font-normal">
                      {currentToMeta.name}
                    </span>
                  </div>
                  <ChevronDown className="w-4 h-4 text-[#6B7280] shrink-0" />
                </button>

                {/* Dropdown Menu */}
                {toDropdownOpen && (
                  <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-xl shadow-lg max-h-64 overflow-y-auto p-2 space-y-1">
                    <div className="p-1 sticky top-0 bg-white border-b border-[#E5E7EB]">
                      <input
                        type="text"
                        placeholder="Search currency, code or country..."
                        value={toSearchQuery}
                        onChange={(e) => setToSearchQuery(e.target.value)}
                        className="w-full px-2.5 py-1.5 text-xs bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg focus:outline-hidden"
                        autoFocus
                      />
                    </div>
                    {filteredToCurrencies.map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => {
                          setToCurrency(c.code);
                          setToDropdownOpen(false);
                          setToSearchQuery('');
                        }}
                        className={`w-full px-2.5 py-2 text-left rounded-lg text-xs flex items-center justify-between hover:bg-[#F3F4F6] cursor-pointer ${
                          toCurrency === c.code ? 'bg-[#F0F4FF] text-[#1E40AF] font-bold' : 'text-[#1A1A1A]'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span>{c.flagEmoji}</span>
                          <span className="font-bold">{c.code}</span>
                          <span className="text-[#6B7280] text-[11px] truncate max-w-[140px]">
                            {c.name}
                          </span>
                        </div>
                        {c.isMajor && (
                          <span className="text-[9px] font-bold bg-[#E5E7EB] px-1.5 py-0.5 rounded text-[#374151]">
                            Major
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Converted Result Display Hero */}
            <div className="p-6 bg-[#FBFBFC] rounded-2xl border border-[#E5E7EB] flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-1">
                <div className="text-xs font-semibold text-[#6B7280]">
                  {financialFormat(amount, fromCurrency)} {currentFromMeta.name} =
                </div>
                <div className="text-3xl sm:text-4xl font-extrabold text-[#1A1A1A] tracking-tight flex items-baseline gap-2">
                  <span>{financialFormat(conversionResult.convertedAmount, toCurrency)}</span>
                  <span className="text-lg font-bold text-[#6B7280]">{toCurrency}</span>
                </div>
                <div className="text-xs text-[#0E7044] font-semibold flex items-center gap-1 pt-1">
                  <span>1 {fromCurrency} = {formatRateDisplay(conversionResult.rate)} {toCurrency}</span>
                  <span className="text-[#9CA3AF]">•</span>
                  <span>1 {toCurrency} = {formatRateDisplay(conversionResult.inverseRate)} {fromCurrency}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleCopyResult}
                  className="px-3 py-2 bg-white border border-[#E5E7EB] rounded-xl text-xs font-bold text-[#1A1A1A] hover:bg-[#F3F4F6] transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-[#0E7044]" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copied ? 'Copied!' : 'Copy Result'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleToggleFavorite(`${fromCurrency}/${toCurrency}`)}
                  className={`px-3 py-2 border rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs ${
                    favoritesList.includes(`${fromCurrency}/${toCurrency}`)
                      ? 'bg-[#FEF9C3] text-[#854D0E] border-[#FDE047]'
                      : 'bg-white text-[#4B5563] border-[#E5E7EB] hover:bg-[#F9FAFB]'
                  }`}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>
                    {favoritesList.includes(`${fromCurrency}/${toCurrency}`) ? 'Pinned' : 'Pin to Watchlist'}
                  </span>
                </button>
              </div>
            </div>

            {/* Formula & Provenance Footnote */}
            <div className="grid sm:grid-cols-3 gap-3 pt-2 text-xs">
              <div className="p-3 bg-[#F9FAFB] rounded-xl border border-[#F3F4F6] space-y-0.5">
                <div className="text-[11px] font-bold text-[#6B7280]">Rate Classification</div>
                <div className="font-semibold text-[#1A1A1A] flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#0E7044]" />
                  <span>{conversionResult.rateType.replace('_', ' ')}</span>
                </div>
              </div>

              <div className="p-3 bg-[#F9FAFB] rounded-xl border border-[#F3F4F6] space-y-0.5">
                <div className="text-[11px] font-bold text-[#6B7280]">Calculation Formula</div>
                <div className="font-semibold text-[#1A1A1A] font-mono text-[11px]">
                  {conversionResult.formulaUsed}
                </div>
              </div>

              <div className="p-3 bg-[#F0FDF4] rounded-xl border border-[#DCFCE7] space-y-0.5">
                <div className="text-[11px] font-bold text-[#0E7044]">Estimated Spread Savings</div>
                <div className="font-bold text-[#0E7044]">
                  ~ ${conversionResult.spreadSavingsEstimated.toFixed(2)} vs Bank Markups
                </div>
              </div>
            </div>
          </div>

          {/* 4. Popular Amounts Conversion Matrix */}
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-4">
            <div className="border-b border-[#E5E7EB] pb-3">
              <h2 className="text-base font-extrabold text-[#1A1A1A]">
                {fromCurrency} to {toCurrency} Conversion Table
              </h2>
              <p className="text-xs text-[#6B7280]">
                Instant reference for popular cash and remittance amounts.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Direct Table */}
              <div className="overflow-x-auto">
                <div className="text-xs font-bold text-[#1A1A1A] mb-2">
                  Convert {fromCurrency} to {toCurrency}
                </div>
                <table className="w-full text-xs text-left">
                  <thead className="bg-[#F9FAFB] text-[#4B5563] font-bold border-b border-[#E5E7EB]">
                    <tr>
                      <th className="p-2.5 rounded-l-lg">{fromCurrency}</th>
                      <th className="p-2.5 rounded-r-lg">{toCurrency}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {popularAmounts.map((amt) => {
                      const res = fxEngine.convert(amt, fromCurrency, toCurrency);
                      return (
                        <tr key={amt} className="hover:bg-[#FBFBFC]">
                          <td className="p-2.5 font-semibold text-[#1A1A1A]">
                            {amt.toLocaleString()} {fromCurrency}
                          </td>
                          <td className="p-2.5 font-mono text-[#0E7044] font-bold">
                            {financialFormat(res.convertedAmount, toCurrency)} {toCurrency}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Inverse Table */}
              <div className="overflow-x-auto">
                <div className="text-xs font-bold text-[#1A1A1A] mb-2">
                  Convert {toCurrency} to {fromCurrency}
                </div>
                <table className="w-full text-xs text-left">
                  <thead className="bg-[#F9FAFB] text-[#4B5563] font-bold border-b border-[#E5E7EB]">
                    <tr>
                      <th className="p-2.5 rounded-l-lg">{toCurrency}</th>
                      <th className="p-2.5 rounded-r-lg">{fromCurrency}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {popularAmounts.map((amt) => {
                      const res = fxEngine.convert(amt, toCurrency, fromCurrency);
                      return (
                        <tr key={amt} className="hover:bg-[#FBFBFC]">
                          <td className="p-2.5 font-semibold text-[#1A1A1A]">
                            {amt.toLocaleString()} {toCurrency}
                          </td>
                          <td className="p-2.5 font-mono text-[#1E40AF] font-bold">
                            {financialFormat(res.convertedAmount, fromCurrency)} {fromCurrency}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* 5. Transfer Provider Bridge CTA */}
          <div className="p-6 bg-[#F0F4FF] border border-[#C7D7FE] rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="text-xs font-bold uppercase tracking-wider text-[#1E40AF]">
                Cross-Border Transfer Comparison
              </div>
              <h3 className="text-sm font-extrabold text-[#1E3A8A]">
                Sending money from {currentFromMeta.name} to {currentToMeta.name}?
              </h3>
              <p className="text-xs text-[#3B82F6]">
                Traditional banks often charge hidden FX markup fees up to 3.8%. Compare verified remittance providers.
              </p>
            </div>
            <button
              onClick={() => {
                let event;
                try {
                  event = new CustomEvent('navigate-tool', { detail: 'transfer-calculator' });
                } catch (e) {
                  event = document.createEvent('CustomEvent');
                  (event as any).initCustomEvent('navigate-tool', false, false, 'transfer-calculator');
                }
                window.dispatchEvent(event);
              }}
              className="px-4 py-2 bg-[#1E40AF] text-white rounded-xl text-xs font-bold hover:bg-[#1E3A8A] transition-colors cursor-pointer shrink-0 text-center"
            >
              Compare Remittance Corridors
            </button>
          </div>

          {/* Market Heatmap CTA Banner */}
          <div className="p-4 bg-linear-to-r from-amber-500/10 via-orange-500/10 to-rose-500/10 border border-amber-200/80 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0 shadow-xs">
                <Flame className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-[#1A1A1A]">
                  Real-Time FX Market Volatility Heatmap
                </h4>
                <p className="text-[11px] text-[#6B7280]">
                  Inspect cross-currency price volatility, 24h spread variance, and daily amplitude across G8 majors & emerging pairs.
                </p>
              </div>
            </div>
            <button
              onClick={() => setActiveTab('heatmap')}
              className="px-3.5 py-1.5 bg-[#1A1A1A] hover:bg-black text-white text-xs font-bold rounded-xl transition-all cursor-pointer shrink-0 self-start sm:self-auto flex items-center gap-1.5 shadow-xs"
            >
              <span>Explore Heatmap</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* TAB: MARKET HEATMAP (D3 Volatility Visualization) */}
      {activeTab === 'heatmap' && (
        <div className="animate-in fade-in duration-150">
          <MarketHeatmap
            onSelectPair={(base, quote) => {
              setFromCurrency(base);
              setToCurrency(quote);
              setActiveTab('converter');
            }}
          />
        </div>
      )}

      {/* 4. TAB 2: HISTORICAL CHARTS & EXACT PAST DATE LOOKUP */}
      {activeTab === 'charts' && (
        <div className="space-y-8 animate-in fade-in duration-150">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-6">
            {/* Chart Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#E5E7EB] pb-4">
              <div>
                <div className="text-xs text-[#6B7280]">Exchange Rate Trend</div>
                <h2 className="text-xl font-extrabold text-[#1A1A1A]">
                  {fromCurrency} to {toCurrency} Historical Performance
                </h2>
              </div>

              {/* Timeframe selector */}
              <div className="flex items-center gap-1 bg-[#F3F4F6] p-1 rounded-xl">
                {(['1D', '5D', '1M', '3M', '6M', 'YTD', '1Y', '5Y', 'MAX'] as const).map((tf) => (
                  <button
                    key={tf}
                    onClick={() => setChartTimeframe(tf)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      chartTimeframe === tf
                        ? 'bg-white text-[#1A1A1A] shadow-xs'
                        : 'text-[#4B5563] hover:text-[#1A1A1A]'
                    }`}
                  >
                    {tf}
                  </button>
                ))}
              </div>
            </div>

            {/* Metrics Row */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-0.5">
                <div className="text-[10px] font-bold uppercase text-[#6B7280]">Current</div>
                <div className="text-sm font-extrabold text-[#1A1A1A]">
                  {formatRateDisplay(conversionResult.rate)}
                </div>
              </div>

              <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-0.5">
                <div className="text-[10px] font-bold uppercase text-[#6B7280]">Period High</div>
                <div className="text-sm font-extrabold text-[#1A1A1A]">
                  {formatRateDisplay(historicalSeries.high)}
                </div>
              </div>

              <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-0.5">
                <div className="text-[10px] font-bold uppercase text-[#6B7280]">Period Low</div>
                <div className="text-sm font-extrabold text-[#1A1A1A]">
                  {formatRateDisplay(historicalSeries.low)}
                </div>
              </div>

              <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-0.5">
                <div className="text-[10px] font-bold uppercase text-[#6B7280]">Period Average</div>
                <div className="text-sm font-extrabold text-[#1A1A1A]">
                  {formatRateDisplay(historicalSeries.average)}
                </div>
              </div>

              <div className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-0.5 col-span-2 sm:col-span-1">
                <div className="text-[10px] font-bold uppercase text-[#6B7280]">Period Change</div>
                <div
                  className={`text-sm font-extrabold flex items-center gap-1 ${
                    historicalSeries.changePct >= 0 ? 'text-[#0E7044]' : 'text-[#B42318]'
                  }`}
                >
                  {historicalSeries.changePct >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                  <span>{Math.abs(historicalSeries.changePct).toFixed(2)}%</span>
                </div>
              </div>
            </div>

            {/* SVG Visual Chart */}
            <div className="p-4 bg-[#FBFBFC] border border-[#E5E7EB] rounded-2xl relative">
              <svg className="w-full h-64 overflow-visible" viewBox="0 0 800 240">
                <defs>
                  <linearGradient id="fxGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1E40AF" stopOpacity="0.15" />
                    <stop offset="100%" stopColor="#1E40AF" stopOpacity="0.0" />
                  </linearGradient>
                </defs>

                {/* Horizontal Grid lines */}
                <line x1="40" y1="30" x2="780" y2="30" stroke="#E5E7EB" strokeDasharray="3 3" />
                <line x1="40" y1="110" x2="780" y2="110" stroke="#E5E7EB" strokeDasharray="3 3" />
                <line x1="40" y1="190" x2="780" y2="190" stroke="#E5E7EB" strokeDasharray="3 3" />

                {/* Grid labels */}
                <text x="30" y="34" fontSize="10" fill="#9CA3AF" textAnchor="end">{formatRateDisplay(historicalSeries.high)}</text>
                <text x="30" y="114" fontSize="10" fill="#9CA3AF" textAnchor="end">{formatRateDisplay(historicalSeries.average)}</text>
                <text x="30" y="194" fontSize="10" fill="#9CA3AF" textAnchor="end">{formatRateDisplay(historicalSeries.low)}</text>

                {/* Path Generation */}
                {(() => {
                  const pts = historicalSeries.points;
                  if (pts.length < 2) return null;
                  const min = historicalSeries.low * 0.998;
                  const max = historicalSeries.high * 1.002;
                  const range = max - min || 1;

                  const coords = pts.map((p, idx) => {
                    const x = 50 + (idx / (pts.length - 1)) * 720;
                    const y = 200 - ((p.rate - min) / range) * 160;
                    return { x, y, point: p };
                  });

                  const pathD = coords.reduce((acc, curr, i) => (i === 0 ? `M ${curr.x} ${curr.y}` : `${acc} L ${curr.x} ${curr.y}`), '');
                  const areaD = `${pathD} L ${coords[coords.length - 1].x} 210 L ${coords[0].x} 210 Z`;

                  return (
                    <>
                      <path d={areaD} fill="url(#fxGrad)" />
                      <path d={pathD} fill="none" stroke="#1E40AF" strokeWidth="2.5" strokeLinecap="round" />
                      {coords.map((c, i) => (
                        <circle
                          key={i}
                          cx={c.x}
                          cy={c.y}
                          r={hoveredPointIndex === i ? 5 : 2}
                          fill={hoveredPointIndex === i ? '#1E40AF' : '#60A5FA'}
                          onMouseEnter={() => setHoveredPointIndex(i)}
                          onMouseLeave={() => setHoveredPointIndex(null)}
                          className="cursor-pointer transition-all"
                        />
                      ))}
                    </>
                  );
                })()}
              </svg>

              {/* Accessible text alternative */}
              <div className="sr-only">
                Historical chart for {fromCurrency} to {toCurrency} over {chartTimeframe}. Low is {historicalSeries.low}, High is {historicalSeries.high}, Average is {historicalSeries.average}.
              </div>
            </div>

            {/* Point Inspector if Hovered */}
            {hoveredPointIndex !== null && historicalSeries.points[hoveredPointIndex] && (
              <div className="p-3 bg-[#1E2022] text-white rounded-xl text-xs flex items-center justify-between">
                <div>
                  <span className="text-[#9CA3AF]">Date: </span>
                  <span className="font-bold">{historicalSeries.points[hoveredPointIndex].date}</span>
                </div>
                <div>
                  <span className="text-[#9CA3AF]">Rate: </span>
                  <span className="font-mono font-bold text-[#60A5FA]">
                    1 {fromCurrency} = {formatRateDisplay(historicalSeries.points[hoveredPointIndex].rate)} {toCurrency}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Exact Past Date Converter Tool */}
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-4">
            <div className="border-b border-[#E5E7EB] pb-3">
              <h3 className="text-base font-extrabold text-[#1A1A1A]">
                Historical Date Lookup Tool
              </h3>
              <p className="text-xs text-[#6B7280]">
                Query official historical fixing rates published by central banks on any specific date.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 items-center">
              <div className="space-y-1">
                <label className="block text-xs font-bold text-[#4B5563]">Select Historical Date</label>
                <input
                  type="date"
                  value={historicalDate}
                  onChange={(e) => setHistoricalDate(e.target.value)}
                  className="w-full px-3 py-2 text-xs font-bold bg-[#FBFBFC] border border-[#E5E7EB] rounded-xl focus:bg-white"
                />
              </div>

              <div className="sm:col-span-2 p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <div className="text-xs text-[#6B7280]">
                    Rate on {historicalDate}:
                  </div>
                  <div className="text-lg font-extrabold text-[#1A1A1A] font-mono">
                    1 {fromCurrency} = {formatRateDisplay(historicalDateLookup.rate)} {toCurrency}
                  </div>
                  {historicalDateLookup.isNonBusinessDayFallback && (
                    <div className="text-[11px] text-amber-700 font-medium">
                      Note: Weekend/Holiday — using latest available official rate.
                    </div>
                  )}
                </div>
                <div className="text-xs text-right text-[#4B5563]">
                  <div>{amount} {fromCurrency} was worth</div>
                  <div className="font-extrabold text-[#1E40AF]">
                    {financialFormat(amount * historicalDateLookup.rate, toCurrency)} {toCurrency}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 5. TAB 3: 170+ CURRENCY DIRECTORY & METADATA PROFILES */}
      {activeTab === 'directory' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 shadow-xs space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#E5E7EB] pb-4">
              <div>
                <h2 className="text-lg font-extrabold text-[#1A1A1A]">
                  Global 170+ Currency Directory
                </h2>
                <p className="text-xs text-[#6B7280]">
                  Official ISO 4217 fiat, special monetary units, and sovereign peg rules.
                </p>
              </div>

              {/* Region Filter */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {['ALL', 'North America', 'Europe', 'Asia-Pacific', 'Latin America', 'Middle East', 'Africa'].map((reg) => (
                  <button
                    key={reg}
                    onClick={() => setDirectoryRegionFilter(reg)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      directoryRegionFilter === reg
                        ? 'bg-[#1A1A1A] text-white'
                        : 'bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB]'
                    }`}
                  >
                    {reg}
                  </button>
                ))}
              </div>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by currency code (e.g. USD, EUR, XAF, NGN), name, or country..."
                value={directorySearch}
                onChange={(e) => setDirectorySearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 text-xs bg-[#FBFBFC] border border-[#E5E7EB] rounded-xl focus:bg-white"
              />
            </div>

            {/* Currency Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
              {filteredDirectoryCurrencies.map((curr) => (
                <div
                  key={curr.code}
                  className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-2 hover:border-[#9CA3AF] transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{curr.flagEmoji}</span>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="font-extrabold text-sm text-[#1A1A1A]">{curr.code}</span>
                          <span className="text-xs text-[#6B7280]">({curr.symbol})</span>
                        </div>
                        <div className="text-xs font-semibold text-[#374151] truncate max-w-[170px]">
                          {curr.name}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        setFromCurrency('USD');
                        setToCurrency(curr.code);
                        setActiveTab('converter');
                      }}
                      className="px-2 py-1 bg-white border border-[#E5E7EB] rounded-md text-[10px] font-bold text-[#1E40AF] hover:bg-[#F0F4FF] cursor-pointer"
                    >
                      Convert
                    </button>
                  </div>

                  <div className="text-[11px] text-[#6B7280] space-y-0.5 border-t border-[#F3F4F6] pt-2">
                    <div>
                      <span className="font-medium text-[#4B5563]">Monetary Authority: </span>
                      <span className="truncate">{curr.monetaryAuthority}</span>
                    </div>
                    <div>
                      <span className="font-medium text-[#4B5563]">Minor Units: </span>
                      <span>{curr.minorUnits} decimals</span>
                    </div>
                    {curr.pegInfo && (
                      <div className="text-[#0E7044] font-semibold flex items-center gap-1">
                        <Lock className="w-3 h-3" />
                        <span>Pegged to {curr.pegInfo.peggedToCurrency} ({curr.pegInfo.fixedRate})</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 6. TAB 4: PAIR DEEP-DIVE & SEO CORRIDOR INTELLIGENCE */}
      {activeTab === 'pair-deep-dive' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#E5E7EB] pb-4">
              <div>
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#F0F4FF] text-[#1E40AF] border border-[#C7D7FE]">
                  Institutional Pair Explorer
                </span>
                <h2 className="text-xl font-extrabold text-[#1A1A1A] mt-1">
                  Corridor Market Analysis & Programmatic SEO Spec
                </h2>
              </div>

              {/* Pair Switcher */}
              <select
                value={selectedPairCode}
                onChange={(e) => setSelectedPairCode(e.target.value)}
                className="px-3 py-2 text-xs font-bold bg-[#FBFBFC] border border-[#E5E7EB] rounded-xl focus:bg-white"
              >
                {SEO_ELIGIBLE_PAIRS.map((p) => (
                  <option key={p.pair} value={p.pair}>
                    {p.pair} — {p.titleTemplate.split('|')[0]}
                  </option>
                ))}
              </select>
            </div>

            {/* Active Pair Deep Dive Card */}
            {(() => {
              const pairData = SEO_ELIGIBLE_PAIRS.find((p) => p.pair === selectedPairCode) || SEO_ELIGIBLE_PAIRS[0];
              const obs = fxEngine.getPairObservation(pairData.base, pairData.quote);
              return (
                <div className="space-y-6">
                  {/* Pair Hero */}
                  <div className="p-6 bg-[#FBFBFC] rounded-2xl border border-[#E5E7EB] flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="text-xs font-semibold text-[#6B7280]">
                        {pairData.base} / {pairData.quote} Exchange Rate
                      </div>
                      <div className="text-3xl font-extrabold text-[#1A1A1A] font-mono">
                        1 {pairData.base} = {formatRateDisplay(obs.rate)} {pairData.quote}
                      </div>
                      <div className="text-xs text-[#0E7044] font-semibold">
                        24h Range: {formatRateDisplay(obs.low24h)} - {formatRateDisplay(obs.high24h)} ({obs.change24hPct >= 0 ? '+' : ''}{obs.change24hPct.toFixed(2)}%)
                      </div>
                    </div>

                    <div className="space-y-1 text-right">
                      <div className="text-xs font-bold text-[#1E40AF]">SEO Eligibility Status</div>
                      <span className="px-2.5 py-1 bg-[#F0FDF4] text-[#0E7044] border border-[#DCFCE7] rounded-full text-xs font-bold">
                        {pairData.status} (Quality: {pairData.dataQualityScore}%)
                      </span>
                    </div>
                  </div>

                  {/* Insights */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">
                      Data-Driven Unique Insights & Economic Context
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {pairData.uniqueInsights.map((insight, idx) => (
                        <div key={idx} className="p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] text-xs text-[#374151] leading-relaxed">
                          {insight}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Canonical & Hreflang Tags */}
                  <div className="p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] space-y-2 text-xs">
                    <div className="font-bold text-[#1A1A1A]">SEO Hreflang Mapping & Canonicals</div>
                    <div className="font-mono text-[11px] text-[#4B5563] space-y-1">
                      <div><span className="text-[#1E40AF] font-bold">Canonical: </span>{pairData.canonicalUrl}</div>
                      {Object.entries(pairData.hreflang).map(([lang, url]) => (
                        <div key={lang}>
                          <span className="text-[#0E7044] font-bold">{lang}: </span>{url}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* 7. TAB 5: MY WATCHLIST & FAVORITES */}
      {activeTab === 'watchlist' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-4">
            <div className="border-b border-[#E5E7EB] pb-3 flex items-center justify-between">
              <div>
                <h2 className="text-base font-extrabold text-[#1A1A1A]">
                  My Pinned Currency Pairs
                </h2>
                <p className="text-xs text-[#6B7280]">
                  Track high-priority FX corridors with 24h movements and instant conversion shortcuts.
                </p>
              </div>
              <button
                onClick={() => setFavoritesList([])}
                className="text-xs text-[#B42318] hover:underline font-semibold cursor-pointer"
              >
                Clear All
              </button>
            </div>

            {favoritesList.length === 0 ? (
              <div className="p-8 text-center text-xs text-[#6B7280] space-y-2">
                <Sparkles className="w-6 h-6 mx-auto text-[#9CA3AF]" />
                <p>You have not pinned any currency pairs yet.</p>
                <p>Pin currency pairs from the Universal Converter or Directory.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {favoritesList.map((pairKey) => {
                  const [b, q] = pairKey.split('/');
                  const obs = fxEngine.getPairObservation(b, q);
                  return (
                    <div
                      key={pairKey}
                      className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-3 hover:border-[#1A1A1A] transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-extrabold text-sm text-[#1A1A1A]">
                          {pairKey}
                        </div>
                        <button
                          onClick={() => handleToggleFavorite(pairKey)}
                          className="text-[#9CA3AF] hover:text-[#B42318] cursor-pointer text-xs"
                        >
                          ✕
                        </button>
                      </div>

                      <div className="font-mono text-lg font-extrabold text-[#1A1A1A]">
                        {formatRateDisplay(obs.rate)}
                      </div>

                      <div className="flex items-center justify-between text-xs pt-1 border-t border-[#F3F4F6]">
                        <span
                          className={`font-bold flex items-center gap-0.5 ${
                            obs.change24hPct >= 0 ? 'text-[#0E7044]' : 'text-[#B42318]'
                          }`}
                        >
                          {obs.change24hPct >= 0 ? '+' : ''}{obs.change24hPct.toFixed(2)}%
                        </span>

                        <button
                          onClick={() => {
                            setFromCurrency(b);
                            setToCurrency(q);
                            setActiveTab('converter');
                          }}
                          className="text-xs font-bold text-[#1E40AF] hover:underline cursor-pointer"
                        >
                          Convert →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 8. TAB 6: RATE ALERTS & NOTIFICATIONS */}
      {activeTab === 'alerts' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-6">
            <div className="border-b border-[#E5E7EB] pb-3">
              <h2 className="text-base font-extrabold text-[#1A1A1A]">
                Exchange Rate Alert Engine
              </h2>
              <p className="text-xs text-[#6B7280]">
                Configure target rate thresholds with built-in anti-flapping hysteresis and multi-channel delivery.
              </p>
            </div>

            {/* Create Alert Form */}
            <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-4">
              <div className="text-xs font-bold text-[#1A1A1A]">Create New Rate Alert</div>

              <div className="grid sm:grid-cols-4 gap-3 items-end">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-[#4B5563]">Currency Pair</label>
                  <select
                    value={alertFormPair}
                    onChange={(e) => setAlertFormPair(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-white border border-[#E5E7EB] rounded-lg font-bold"
                  >
                    <option value="USD/EUR">USD/EUR</option>
                    <option value="GBP/USD">GBP/USD</option>
                    <option value="USD/JPY">USD/JPY</option>
                    <option value="USD/CAD">USD/CAD</option>
                    <option value="USD/INR">USD/INR</option>
                    <option value="EUR/XAF">EUR/XAF</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-[#4B5563]">Condition</label>
                  <select
                    value={alertFormCondition}
                    onChange={(e) => setAlertFormCondition(e.target.value as any)}
                    className="w-full px-3 py-2 text-xs bg-white border border-[#E5E7EB] rounded-lg font-bold"
                  >
                    <option value="ABOVE">Rate Rises Above (≥)</option>
                    <option value="BELOW">Rate Drops Below (≤)</option>
                    <option value="PERCENT_CHANGE">Moves by 1.0%</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-[#4B5563]">Target Threshold</label>
                  <input
                    type="number"
                    step="0.001"
                    value={alertFormTarget}
                    onChange={(e) => setAlertFormTarget(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 text-xs bg-white border border-[#E5E7EB] rounded-lg font-bold"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const [b, q] = alertFormPair.split('/');
                    const newA = fxEngine.createAlert({
                      baseCurrency: b,
                      quoteCurrency: q,
                      condition: alertFormCondition,
                      targetValue: alertFormTarget,
                      channel: 'IN_APP',
                      frequency: 'INSTANT',
                      active: true,
                      cooldownMinutes: 60,
                      hysteresisPct: 0.25,
                    });
                    setAlerts(fxEngine.getAlerts());
                    setAlertSuccessMsg(`Alert successfully configured for ${alertFormPair}`);
                    setTimeout(() => setAlertSuccessMsg(null), 3000);
                  }}
                  className="w-full py-2 bg-[#1A1A1A] text-white rounded-lg text-xs font-bold hover:bg-[#374151] transition-colors cursor-pointer"
                >
                  Create Alert
                </button>
              </div>

              {alertSuccessMsg && (
                <div className="text-xs font-bold text-[#0E7044] flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>{alertSuccessMsg}</span>
                </div>
              )}
            </div>

            {/* Active Alerts List */}
            <div className="space-y-2">
              <div className="text-xs font-bold text-[#1A1A1A]">Active Alerts ({alerts.length})</div>
              {alerts.map((al) => (
                <div
                  key={al.id}
                  className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] flex items-center justify-between text-xs"
                >
                  <div className="flex items-center gap-3">
                    <Bell className={`w-4 h-4 ${al.active ? 'text-[#0E7044]' : 'text-[#9CA3AF]'}`} />
                    <div>
                      <span className="font-extrabold">{al.baseCurrency}/{al.quoteCurrency}</span>
                      <span className="text-[#6B7280]"> {al.condition} </span>
                      <span className="font-mono font-bold">{al.targetValue}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        fxEngine.toggleAlert(al.id);
                        setAlerts(fxEngine.getAlerts());
                      }}
                      className={`px-2.5 py-1 rounded text-[11px] font-bold cursor-pointer ${
                        al.active ? 'bg-[#DCFCE7] text-[#0E7044]' : 'bg-[#F3F4F6] text-[#6B7280]'
                      }`}
                    >
                      {al.active ? 'Active' : 'Paused'}
                    </button>
                    <button
                      onClick={() => {
                        fxEngine.deleteAlert(al.id);
                        setAlerts(fxEngine.getAlerts());
                      }}
                      className="text-[#B42318] text-xs font-bold hover:underline cursor-pointer"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 bg-[#F9FAFB] rounded-xl border border-[#F3F4F6] text-[11px] text-[#6B7280] leading-relaxed">
              <strong>Disclaimer:</strong> YamooPay Exchange Rate Alerts are informational notifications for consumer and commercial convenience. They do not constitute financial advice, automated trading orders, or execution guarantees.
            </div>
          </div>
        </div>
      )}

      {/* 9. TAB 7: OFFLINE & PWA ARCHITECTURE */}
      {activeTab === 'offline-pwa' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-4">
            <div className="border-b border-[#E5E7EB] pb-3">
              <h2 className="text-base font-extrabold text-[#1A1A1A]">
                PWA Service Worker & Offline Cache Policy
              </h2>
              <p className="text-xs text-[#6B7280]">
                Continuous conversion availability during offline travel or rural connectivity drops.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-2">
                <div className="text-xs font-bold text-[#1A1A1A] flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[#0E7044]" />
                  <span>Honest LKG Metadata Storage</span>
                </div>
                <p className="text-xs text-[#6B7280] leading-relaxed">
                  When offline, the client accesses the latest verified observations cached during the previous successful sync, accompanied by an explicit timestamp disclosure badge.
                </p>
              </div>

              <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-2">
                <div className="text-xs font-bold text-[#1A1A1A] flex items-center gap-2">
                  <RotateCcw className="w-4 h-4 text-[#1E40AF]" />
                  <span>Silent Background Reconnect</span>
                </div>
                <p className="text-xs text-[#6B7280] leading-relaxed">
                  Upon network reconnection, the engine fetches fresh observations in the background without clearing user input amounts or disrupting ongoing calculations.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 10. TAB 8: METHODOLOGY & SOURCE TRANSPARENCY */}
      {activeTab === 'methodology' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-4">
            <div className="border-b border-[#E5E7EB] pb-3">
              <h2 className="text-base font-extrabold text-[#1A1A1A]">
                How YamooPay Calculates Exchange Rates
              </h2>
              <p className="text-xs text-[#6B7280]">
                Transparent methodology disclosure for institutional trust and regulatory compliance.
              </p>
            </div>

            <div className="space-y-4 text-xs text-[#374151] leading-relaxed">
              <div className="p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] space-y-1.5">
                <h4 className="font-bold text-[#1A1A1A]">1. What is the Mid-Market Rate?</h4>
                <p>
                  The mid-market rate represents the true arithmetic midpoint between wholesale buy (bid) and sell (ask) prices in global interbank foreign exchange markets. It carries zero retail markup spread and serves as the unbiased gold standard for fair currency comparison.
                </p>
              </div>

              <div className="p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] space-y-1.5">
                <h4 className="font-bold text-[#1A1A1A]">2. Central Bank Statutory Reference Fixings</h4>
                <p>
                  For official audit, tax, and customs calculations, YamooPay ingests daily official benchmark fixings published by central monetary authorities (e.g. ECB at 16:00 CET, Federal Reserve H.10 series, Bank of England).
                </p>
              </div>

              <div className="p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] space-y-1.5">
                <h4 className="font-bold text-[#1A1A1A]">3. Cross-Rate Derivation & Consistency</h4>
                <p>
                  Non-major currency pairs are derived through shortest trusted intermediary paths (typically USD or EUR bridges) with automated triangular arbitrage consistency checks ($A/B \times B/C \approx A/C$).
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 11. TAB 9: FX COMMAND CENTER (ADMIN & OPERATIONS COCKPIT) */}
      {activeTab === 'command-center' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[#E5E7EB] pb-4">
              <div>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-[#F0F4FF] text-[#1E40AF] border border-[#C7D7FE]">
                  Operations Cockpit (Light Mode)
                </span>
                <h2 className="text-xl font-extrabold text-[#1A1A1A] mt-1">
                  Exchange Rate Command Center
                </h2>
              </div>

              <div className="flex items-center gap-2">
                {telemetry.failoverActive ? (
                  <button
                    onClick={() => {
                      fxEngine.triggerFailback();
                      setTelemetry(fxEngine.getTelemetry());
                    }}
                    className="px-3 py-1.5 bg-[#0E7044] text-white rounded-lg text-xs font-bold hover:bg-[#0B5936] cursor-pointer"
                  >
                    Restore Primary Provider (Failback)
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      fxEngine.triggerManualFailover();
                      setTelemetry(fxEngine.getTelemetry());
                    }}
                    className="px-3 py-1.5 bg-[#B42318] text-white rounded-lg text-xs font-bold hover:bg-[#912018] cursor-pointer"
                  >
                    Simulate Provider Failover
                  </button>
                )}
              </div>
            </div>

            {/* Telemetry KPIs Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1">
                <div className="text-[11px] font-bold text-[#6B7280]">Active Ingestion Stream</div>
                <div className="text-sm font-extrabold text-[#1A1A1A] truncate">{telemetry.activeSource}</div>
                <div className="text-[10px] text-[#0E7044] font-semibold">Latency: {telemetry.avgLatencyMs}ms</div>
              </div>

              <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1">
                <div className="text-[11px] font-bold text-[#6B7280]">Currency Pairs Tracked</div>
                <div className="text-sm font-extrabold text-[#1A1A1A]">{telemetry.totalPairsComputed.toLocaleString()}</div>
                <div className="text-[10px] text-[#0E7044] font-semibold">{telemetry.freshPairsCount} Fresh (99.1%)</div>
              </div>

              <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1">
                <div className="text-[11px] font-bold text-[#6B7280]">Monthly Provider Quota</div>
                <div className="text-sm font-extrabold text-[#1A1A1A]">{telemetry.monthlyQuotaBurnPct}% Burn</div>
                <div className="text-[10px] text-[#6B7280]">{(telemetry.monthlyQuotaUsed / 1000000).toFixed(2)}M / 5.0M Calls</div>
              </div>

              <div className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-1">
                <div className="text-[11px] font-bold text-[#6B7280]">Cache Hit Ratio</div>
                <div className="text-sm font-extrabold text-[#1E40AF]">{telemetry.cacheHitRatio}%</div>
                <div className="text-[10px] text-[#0E7044] font-semibold">Zero Direct Client Secrets</div>
              </div>
            </div>

            {/* Provider Source Status Cards */}
            <div className="grid sm:grid-cols-3 gap-4 text-xs">
              <div className="p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[#1A1A1A]">Tier B Commercial Stream</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${telemetry.primaryProviderStatus === 'HEALTHY' ? 'bg-[#DCFCE7] text-[#0E7044]' : 'bg-[#FEF3F2] text-[#B42318]'}`}>
                    {telemetry.primaryProviderStatus}
                  </span>
                </div>
                <div className="text-[#6B7280] text-[11px]">Primary wholesale market liquidity pool.</div>
              </div>

              <div className="p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[#1A1A1A]">Tier A Central Banks</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#DCFCE7] text-[#0E7044]">
                    HEALTHY
                  </span>
                </div>
                <div className="text-[#6B7280] text-[11px]">ECB, Fed, BoE, BoC daily statutory fixings.</div>
              </div>

              <div className="p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[#1A1A1A]">Tier C Secondary Fallback</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#DCFCE7] text-[#0E7044]">
                    STANDBY READY
                  </span>
                </div>
                <div className="text-[#6B7280] text-[11px]">Independent consensus & failover stream.</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 12. TAB 10: ARCHITECTURAL DELIVERABLES (A → AL) & ADRs */}
      {activeTab === 'deliverables-p4' && (
        <div className="space-y-6 animate-in fade-in duration-150">
          {/* ADR Records */}
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-4">
            <div className="border-b border-[#E5E7EB] pb-3">
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#F0F4FF] text-[#1E40AF] border border-[#C7D7FE]">
                Architecture Decision Records (ADRs)
              </span>
              <h2 className="text-lg font-extrabold text-[#1A1A1A] mt-1">
                Prompt 04 Architectural Decisions
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {FX_ADR_RECORDS.map((adr) => (
                <div key={adr.id} className="p-4 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-[#1E40AF]">{adr.id}</span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#DCFCE7] text-[#0E7044]">
                      {adr.status}
                    </span>
                  </div>
                  <div className="font-bold text-[#1A1A1A]">{adr.title}</div>
                  <p className="text-[#4B5563] text-[11px] leading-relaxed">{adr.context}</p>
                  <div className="p-2 bg-[#F3F4F6] rounded-lg text-[11px] font-mono text-[#374151]">
                    <strong>Decision: </strong>{adr.decision}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Deliverables List (A - AL) */}
          <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-4">
            <div className="border-b border-[#E5E7EB] pb-3">
              <h3 className="text-base font-extrabold text-[#1A1A1A]">
                Prompt 04 Deliverables Index (A → AL, 38 Specifications)
              </h3>
              <p className="text-xs text-[#6B7280]">
                Formalized specification breakdown for the Global Currency Intelligence Engine.
              </p>
            </div>

            <div className="space-y-3">
              {PROMPT_04_DELIVERABLES.map((del) => (
                <div key={del.code} className="p-3 bg-[#FBFBFC] rounded-xl border border-[#E5E7EB] text-xs space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-extrabold text-[#1E40AF]">
                      Section {del.code}: {del.titleEn}
                    </span>
                    <span className="text-[10px] font-bold bg-[#E5E7EB] text-[#374151] px-2 py-0.5 rounded">
                      {del.category}
                    </span>
                  </div>
                  <p className="text-[#4B5563]">{del.summary}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
